import axios from 'axios';
import { normalizePaginatedResponse } from '../utils/apiPagination';
import { filenameFromContentDisposition, resolveDownloadFilename } from '../utils/downloadFilename';

// Si está vacío o no definido, se usa ruta relativa /api (cuando hay Nginx como proxy)
const API_URL = process.env.REACT_APP_API_URL || '';
const baseURL = API_URL ? `${API_URL}/api` : '/api';

const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

const descargarBlobAutenticado = async (path, nombreFallback = 'archivo') => {
  const response = await api.get(path, { responseType: 'blob' });
  const fromHeader = filenameFromContentDisposition(
    response.headers['content-disposition'],
    nombreFallback
  );
  const filename = resolveDownloadFilename(fromHeader, nombreFallback);
  triggerBlobDownload(response.data, filename);
};

// Crear instancia de axios con configuración base
const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token a las peticiones (DRF espera "Token <key>")
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    // FormData necesita multipart con boundary; no forzar application/json
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const headers = config.headers;
      if (headers) {
        if (typeof headers.set === 'function') {
          headers.set('Content-Type', false);
        } else {
          delete headers['Content-Type'];
          delete headers['content-type'];
        }
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Ante 401, limpiar sesión y redirigir a login (evita pantallas en blanco por token inválido)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      const path = window.location?.pathname || '';
      if (!path.startsWith('/login') && !path.startsWith('/register')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Servicios de autenticación
export const authService = {
  // Registro
  register: async (userData) => {
    const response = await api.post('/auth/register/', userData);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  // Login
  login: async (credentials) => {
    const response = await api.post('/auth/login/', credentials);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  // Logout
  logout: async () => {
    try {
      await api.post('/auth/logout/');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  },

  // Obtener perfil
  getProfile: async () => {
    const response = await api.get('/auth/profile/');
    return response.data;
  },

  // Obtener perfil extendido
  getProfileExtended: async () => {
    const response = await api.get('/auth/profile/extended/');
    return response.data;
  },

  // Actualizar perfil
  updateProfile: async (profileData) => {
    const response = await api.patch('/auth/profile/update/', profileData);
    return response.data;
  },

  // Subir foto de perfil (avatar). options: { onUploadProgress(percent) }
  uploadAvatar: async (file, options = {}) => {
    const formData = new FormData();
    formData.append('avatar', file);
    const token = localStorage.getItem('token');
    const url = `${baseURL}/auth/profile/update/`;
    const config = {
      headers: { Authorization: token ? `Token ${token}` : '' },
    };
    if (options.onUploadProgress) {
      config.onUploadProgress = (e) => {
        if (e.lengthComputable) options.onUploadProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    const response = await axios.patch(url, formData, config);
    return response.data;
  },

  // Cambiar contraseña (current_password, new_password)
  changePassword: async (currentPassword, newPassword) => {
    const response = await api.post('/auth/profile/change-password/', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  },

  // Obtener grupos
  getGroups: async () => {
    const response = await api.get('/auth/groups/');
    return response.data;
  },

  // Obtener todos los grupos disponibles
  getAllGroups: async () => {
    const response = await api.get('/auth/groups/all/');
    return response.data;
  },

  // Verificar si el usuario está autenticado
  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },

  // Obtener usuario del localStorage
  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },
};

// Servicios para opciones de desplegables
export const optionsService = {
  // Obtener géneros
  getGeneros: async () => {
    const response = await api.get('/auth/options/generos/');
    return response.data;
  },

  // Obtener países
  getPaises: async () => {
    const response = await api.get('/auth/options/paises/');
    return response.data;
  },

  // Obtener provincias por país
  getProvincias: async (paisId) => {
    const response = await api.get(`/auth/options/provincias/?pais_id=${paisId}`);
    return response.data;
  },

  // Obtener localidades por provincia (en el formulario se usan como Partidos)
  getLocalidades: async (provinciaId) => {
    const response = await api.get(`/auth/options/localidades/?provincia_id=${provinciaId}`);
    return response.data;
  },

  // Obtener barrios por partido (partido_id = localidad_id)
  getBarrios: async (partidoId) => {
    const response = await api.get(`/auth/options/barrios/?partido_id=${partidoId}`);
    return response.data;
  },

  // Obtener tipos de educación secundaria
  getTiposEducacionSecundaria: async () => {
    const response = await api.get('/auth/options/tipos-educacion-secundaria/');
    return response.data;
  },

  // Obtener niveles de estudio
  getNivelesEstudio: async () => {
    const response = await api.get('/auth/options/niveles-estudio/');
    return response.data;
  },

  // Obtener situaciones laborales
  getSituacionesLaborales: async () => {
    const response = await api.get('/auth/options/situaciones-laborales/');
    return response.data;
  },

  // Obtener áreas de trabajo
  getAreasTrabajo: async () => {
    const response = await api.get('/auth/options/areas-trabajo/');
    return response.data;
  },

  // Obtener habilidades (solo predefinidas; no incluye las personalizadas)
  getHabilidades: async () => {
    const response = await api.get('/auth/options/habilidades/');
    return response.data;
  },

  // Obtener o crear habilidad por nombre (para "Otros" en registro). Devuelve { id, nombre }.
  getOrCreateHabilidad: async (nombre) => {
    const response = await api.post('/auth/options/habilidades/obtener-o-crear/', { nombre: nombre.trim() });
    return response.data;
  },

  // Obtener objetivos del curso
  getObjetivosCurso: async () => {
    const response = await api.get('/auth/options/objetivos-curso/');
    return response.data;
  },

  // Obtener niveles del curso (Inicial, Intermedio, Avanzado)
  getNivelesCurso: async () => {
    const response = await api.get('/auth/options/niveles-curso/');
    return response.data;
  },

  // Obtener horas de dedicación
  getHorasDedicacion: async () => {
    const response = await api.get('/auth/options/horas-dedicacion/');
    return response.data;
  },

  // Obtener fuentes de información
  getFuentesInformacion: async () => {
    const response = await api.get('/auth/options/fuentes-informacion/');
    return response.data;
  },
};

// Servicios para gestión de usuarios
export const usersService = {
  // Listar usuarios (por defecto solo activos; para Gestión de usuarios)
  getUsers: async (params = {}) => {
    const response = await api.get('/auth/users/', { params });
    return response.data;
  },
  // Listar solo usuarios inactivos (lista negra)
  getInactiveUsers: async () => {
    const response = await api.get('/auth/users/', { params: { inactive_only: 1 } });
    return response.data;
  },

  // Obtener usuario por ID
  getUser: async (userId) => {
    const response = await api.get(`/auth/users/${userId}/`);
    return response.data;
  },

  // Crear usuario
  createUser: async (userData) => {
    const response = await api.post('/auth/users/create/', userData);
    return response.data;
  },

  // Actualizar usuario
  updateUser: async (userId, userData) => {
    const response = await api.patch(`/auth/users/${userId}/update/`, userData);
    return response.data;
  },

  // Carga masiva de usuarios. options: { onUploadProgress(percent) }
  bulkUploadUsers: async (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    const config = { headers: { 'Content-Type': 'multipart/form-data' } };
    if (options.onUploadProgress) {
      config.onUploadProgress = (e) => {
        if (e.lengthComputable) options.onUploadProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    const response = await api.post('/auth/users/bulk-upload/', formData, config);
    return response.data;
  },

  // Descargar plantilla para carga masiva (format: 'csv' o 'xlsx')
  getBulkUploadTemplate: async (format = 'csv') => {
    const response = await api.get('/bulk-upload-template/', {
      params: { format },
      responseType: 'blob',
    });
    const filename = format === 'xlsx' ? 'carga_masiva_usuarios.xlsx' : 'carga_masiva_usuarios.csv';
    const url = window.URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },
};

// Servicios para reportes (Coordinador)
export const reportesService = {
  getDashboard: async (params = {}) => {
    const response = await api.get('/auth/reportes/dashboard/', { params });
    return response.data;
  },
};

// Servicios para gestión de cursos
export const cursosService = {
  // Listar cursos
  getCursos: async () => {
    const response = await api.get('/auth/cursos/');
    return response.data;
  },

  // Cursos para la home pública (mostrar_en_home=True, no requiere auth)
  getCursosHome: async () => {
    const response = await api.get('/auth/cursos/home/');
    return response.data;
  },

  // Cursos con inscripción abierta → { results, total, page, page_size }
  getCursosDisponibles: async (params = {}) => {
    const response = await api.get('/auth/cursos/disponibles/', { params });
    return normalizePaginatedResponse(response.data);
  },

  // Cursos en los que el estudiante está inscripto
  getMisCursos: async () => {
    const response = await api.get('/auth/cursos/mis-cursos/');
    return response.data;
  },

  // Clases, tareas y entregas solo de los cursos del estudiante (una petición)
  getMisCursosContenido: async () => {
    const response = await api.get('/auth/cursos/mis-cursos/contenido/');
    return response.data;
  },

  // Obtener curso por ID
  getCurso: async (cursoId) => {
    const response = await api.get(`/auth/cursos/${cursoId}/`);
    return response.data;
  },

  // Obtener nombre y descripción por código (para reutilizar al crear/editar)
  getCursoPorCodigo: async (codigo) => {
    const response = await api.get('/auth/cursos/por-codigo/', { params: { codigo: (codigo || '').trim() } });
    return response.data;
  },

  // Crear curso
  createCurso: async (cursoData) => {
    const response = await api.post('/auth/cursos/create/', cursoData);
    return response.data;
  },

  // Actualizar curso
  updateCurso: async (cursoId, cursoData) => {
    const response = await api.patch(`/auth/cursos/${cursoId}/update/`, cursoData);
    return response.data;
  },

  // Eliminar curso (solo Coordinador)
  deleteCurso: async (cursoId) => {
    const response = await api.delete(`/auth/cursos/${cursoId}/delete/`);
    return response.data;
  },

  // Inscribirse en un curso (estudiante)
  inscribirseEnCurso: async (cursoId) => {
    const response = await api.post(`/auth/cursos/${cursoId}/inscribirse/`);
    return response.data;
  },

  // Pre-inscribirse en un curso (estudiante); el profesor confirma en /dashboard/inscripcion
  preInscribirseEnCurso: async (cursoId) => {
    const response = await api.post(`/auth/cursos/${cursoId}/pre-inscribirse/`);
    return response.data;
  },

  // Listar cursos con pre-inscripciones (sin params) o pre-inscriptos paginados (curso_id, page, page_size)
  getPreInscripciones: async (params = {}) => {
    const response = await api.get('/auth/inscripcion/pre-inscripciones/', { params });
    return response.data;
  },

  // Aceptar pre-inscripción (profesor): crea la inscripción real
  aceptarPreInscripcion: async (preInscripcionId) => {
    const response = await api.post(`/auth/inscripcion/pre-inscripciones/${preInscripcionId}/aceptar/`);
    return response.data;
  },

  // Declinar pre-inscripción (profesor): el alumno no podrá volver a pre-inscribirse a ese curso
  declinarPreInscripcion: async (preInscripcionId) => {
    const response = await api.post(`/auth/inscripcion/pre-inscripciones/${preInscripcionId}/declinar/`);
    return response.data;
  },

  // Darse de baja de un curso (estudiante); solo si no está aprobado ni finalizado
  darseDeBajaCurso: async (cursoId) => {
    const response = await api.post(`/auth/cursos/${cursoId}/darse-de-baja/`);
    return response.data;
  },

  // Resumen de estudiantes de un curso (profesor/coordinador)
  getResumenEstudiantes: async (cursoId) => {
    const response = await api.get(`/auth/cursos/${cursoId}/resumen-estudiantes/`);
    return response.data;
  },

  // Candidatos a agregar al curso (estudiantes no inscriptos)
  getCandidatosEstudiantes: async (cursoId, q = '') => {
    const params = q ? { q } : {};
    const response = await api.get(`/auth/cursos/${cursoId}/candidatos-estudiantes/`, { params });
    return response.data;
  },

  // Asignar/Inscribir estudiante manualmente al curso (sin enviar mail)
  agregarEstudianteManual: async (cursoId, userId) => {
    const response = await api.post(`/auth/cursos/${cursoId}/agregar-estudiante/`, { user_id: userId });
    return response.data;
  },

  // Marcar estudiante como aprobado o no (profesor/coordinador); actualiza al instante
  updateEstudianteAprobado: async (cursoId, userId, aprobado) => {
    const response = await api.patch(`/auth/cursos/${cursoId}/estudiantes/${userId}/aprobado/`, { aprobado });
    return response.data;
  },

  // Marcar estudiante como certificado o no (profesor/coordinador). La fecha de emisión es por curso.
  updateEstudianteCertificado: async (cursoId, userId, certificado) => {
    const response = await api.patch(`/auth/cursos/${cursoId}/estudiantes/${userId}/certificado/`, { certificado });
    return response.data;
  },

  // Fecha de emisión de los certificados del curso (una para todo el curso)
  updateFechaCertificadoCurso: async (cursoId, fechaCertificado) => {
    const response = await api.patch(`/auth/cursos/${cursoId}/fecha-certificado/`, { fecha_certificado: fechaCertificado || null });
    return response.data;
  },

  // Plantilla PDF del curso (null = diseño por defecto)
  updatePlantillaCertificadoCurso: async (cursoId, plantillaCertificadoId) => {
    const response = await api.patch(`/auth/cursos/${cursoId}/plantilla-certificado/`, {
      plantilla_certificado_id: plantillaCertificadoId == null ? null : plantillaCertificadoId,
    });
    return response.data;
  },

  updateEntidadCurso: async (cursoId, entidad) => {
    const response = await api.patch(`/auth/cursos/${cursoId}/entidad/`, { entidad: entidad || '' });
    return response.data;
  },

  // Certificado PDF (solo si el curso está aprobado); devuelve blob para ver/descargar
  getCertificadoPdf: async (cursoId) => {
    const response = await api.get(`/auth/cursos/${cursoId}/certificado/`, { responseType: 'blob' });
    return response.data;
  },

  // Certificado PDF para un usuario (solo coordinador); devuelve blob
  getCertificadoPdfForUser: async (cursoId, userId) => {
    const response = await api.get(`/auth/cursos/${cursoId}/certificado/${userId}/`, { responseType: 'blob' });
    return response.data;
  },

  // Estado de certificados por curso finalizado: { curso_id: todos_certificados }
  getCertificadosEstado: async () => {
    const response = await api.get('/auth/certificados/estado/');
    return response.data || {};
  },
};

// Reuniones (cursadas) editables por profesor/tutor
export const reunionesService = {
  list: async () => {
    const response = await api.get('/auth/reuniones/');
    return response.data || [];
  },
  upsert: async (cursoId, fecha, hora) => {
    const response = await api.post(`/auth/cursos/${cursoId}/reuniones/`, { fecha, hora });
    return response.data;
  },
  quitar: async (cursoId, fecha) => {
    const response = await api.delete(`/auth/cursos/${cursoId}/reuniones/quitar/?fecha=${encodeURIComponent(fecha)}`);
    return response.data;
  },
  restaurar: async (cursoId, fecha) => {
    const response = await api.post(`/auth/cursos/${cursoId}/reuniones/restaurar/`, { fecha });
    return response.data;
  },
};

// Servicios para gestión de tareas
export const tareasService = {
  // Listar tareas
  getTareas: async () => {
    const response = await api.get('/auth/tareas/');
    return response.data;
  },

  // Tareas de cursos con el mismo código (para reutilizar contenido/material)
  getTareasReutilizables: async (cursoId, excluirId = null) => {
    const params = { curso_id: cursoId };
    if (excluirId) params.excluir_id = excluirId;
    const response = await api.get('/auth/tareas/reutilizables/', { params });
    return response.data;
  },

  // Obtener tarea por ID
  getTarea: async (tareaId) => {
    const response = await api.get(`/auth/tareas/${tareaId}/`);
    return response.data;
  },

  // Crear tarea. tareaData puede ser objeto JSON o FormData. options: { onUploadProgress(percent) }
  createTarea: async (tareaData, options = {}) => {
    const config = tareaData instanceof FormData ? { headers: { 'Content-Type': false } } : {};
    if (options.onUploadProgress) {
      config.onUploadProgress = (e) => {
        if (e.lengthComputable) options.onUploadProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    const response = await api.post('/auth/tareas/create/', tareaData, config);
    return response.data;
  },

  // Actualizar tarea. options: { onUploadProgress(percent) }
  updateTarea: async (tareaId, tareaData, options = {}) => {
    const config = tareaData instanceof FormData ? { headers: { 'Content-Type': false } } : {};
    if (options.onUploadProgress) {
      config.onUploadProgress = (e) => {
        if (e.lengthComputable) options.onUploadProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    const response = await api.patch(`/auth/tareas/${tareaId}/update/`, tareaData, config);
    return response.data;
  },

  // Eliminar tarea
  deleteTarea: async (tareaId) => {
    const response = await api.delete(`/auth/tareas/${tareaId}/delete/`);
    return response.data;
  },

  // Planilla de estudiantes + estado de entrega / habilitación
  getPlanillaHabilitacion: async (tareaId) => {
    const id = tareaId != null ? String(tareaId) : '';
    const response = await api.get(`/auth/tareas/${id}/planilla-habilitacion/`);
    return response.data;
  },
  habilitarEstudiante: async (tareaId, userId, fechaLimite) => {
    const id = tareaId != null ? String(tareaId) : '';
    const response = await api.post(`/auth/tareas/${id}/habilitar-estudiante/`, {
      user_id: userId,
      fecha_limite: fechaLimite,
    });
    return response.data;
  },
  deshabilitarEstudiante: async (tareaId, userId) => {
    const id = tareaId != null ? String(tareaId) : '';
    const uid = userId != null ? String(userId) : '';
    const response = await api.delete(`/auth/tareas/${id}/habilitar-estudiante/${uid}/`);
    return response.data;
  },

  // Mi entrega de una tarea (estudiante)
  getMiEntrega: async (tareaId) => {
    const id = tareaId != null ? String(tareaId) : '';
    const response = await api.get(`/auth/tareas/${id}/entrega/`);
    return response.data;
  },
  // options: { onUploadProgress(percent) }
  submitEntrega: async (tareaId, formData, options = {}) => {
    const id = tareaId != null ? String(tareaId) : '';
    const config = { headers: { 'Content-Type': undefined } };
    if (options.onUploadProgress) {
      config.onUploadProgress = (e) => {
        if (e.lengthComputable) options.onUploadProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    const response = await api.post(`/auth/tareas/${id}/entrega/submit/`, formData, config);
    return response.data;
  },

  /**
   * Descarga un adjunto de entrega con autenticación.
   * El servidor fuerza descarga (attachment + octet-stream) para que el navegador no ejecute el archivo.
   */
  descargarArchivoEntrega: async (archivoId, nombreFallback = 'archivo') => {
    const id = archivoId != null ? String(archivoId) : '';
    await descargarBlobAutenticado(`/auth/entregas/archivos/${id}/descargar/`, nombreFallback);
  },

  descargarArchivoTarea: async (archivoId, nombreFallback = 'archivo') => {
    const id = archivoId != null ? String(archivoId) : '';
    await descargarBlobAutenticado(`/auth/tareas/archivos/${id}/descargar/`, nombreFallback);
  },

  // Corrección de tareas (profesor/tutor/ayudante)
  getEntregasPendientes: async (params = {}) => {
    const response = await api.get('/auth/entregas/pendientes/', { params });
    return normalizePaginatedResponse(response.data);
  },
  getEntregasCorregidas: async (params = {}) => {
    const response = await api.get('/auth/entregas/corregidas/', { params });
    return normalizePaginatedResponse(response.data);
  },
  getEntregaParaCorregir: async (entregaId, options = {}) => {
    const id = entregaId != null ? String(entregaId) : '';
    const params = {};
    if (options.modo) params.modo = options.modo;
    const response = await api.get(`/auth/entregas/${id}/`, { params });
    return response.data;
  },
  corregirEntrega: async (entregaId, data) => {
    const id = entregaId != null ? String(entregaId) : '';
    const response = await api.patch(`/auth/entregas/${id}/corregir/`, data);
    return response.data;
  },
  getComentariosEntrega: async (entregaId) => {
    const id = entregaId != null ? String(entregaId) : '';
    const response = await api.get(`/auth/entregas/${id}/comentarios/`);
    return response.data;
  },
  crearComentarioEntrega: async (entregaId, texto, rol = null) => {
    const id = entregaId != null ? String(entregaId) : '';
    const rolActivo = rol || localStorage.getItem('selectedRole') || '';
    const response = await api.post(`/auth/entregas/${id}/comentarios/`, {
      texto,
      rol: rolActivo,
    });
    return response.data;
  },
  // Calificaciones del estudiante → { results, total, page, page_size, cursos }
  getCalificaciones: async (params = {}) => {
    const response = await api.get('/auth/calificaciones/', { params });
    const data = response.data;
    return {
      ...normalizePaginatedResponse(data),
      cursos: Array.isArray(data?.cursos) ? data.cursos : [],
    };
  },

  // Grupos de tarea (entrega en grupos): solo tareas con entrega_en_grupos
  getGruposTarea: async (tareaId) => {
    const response = await api.get(`/auth/tareas/${tareaId}/grupos/`);
    return response.data;
  },
  configGruposTarea: async (tareaId, data) => {
    const response = await api.post(`/auth/tareas/${tareaId}/grupos/config/`, data);
    return response.data;
  },
  setGruposInscripcion: async (tareaId, gruposCerrados) => {
    const response = await api.patch(`/auth/tareas/${tareaId}/grupos/inscripcion/`, {
      grupos_cerrados: gruposCerrados,
    });
    return response.data;
  },
  agregarAGrupo: async (tareaId, grupoId, userId) => {
    const response = await api.post(`/auth/tareas/${tareaId}/grupos/${grupoId}/agregar/`, { user_id: userId });
    return response.data;
  },
  quitarDeGrupo: async (tareaId, grupoId, userId) => {
    const response = await api.delete(`/auth/tareas/${tareaId}/grupos/${grupoId}/quitar/${userId}/`);
    return response.data;
  },
  actualizarGrupoTarea: async (tareaId, grupoId, data) => {
    const response = await api.patch(`/auth/tareas/${tareaId}/grupos/${grupoId}/`, data);
    return response.data;
  },

  // Mis grupos (estudiante): tareas con grupos de mis cursos y unirme a un grupo
  getMisGruposTareas: async () => {
    const response = await api.get('/auth/mis-grupos/');
    return response.data;
  },
  unirmeAGrupo: async (tareaId, grupoId) => {
    const response = await api.post(`/auth/tareas/${tareaId}/grupos/${grupoId}/unirme/`);
    return response.data;
  },
  salirDeGrupo: async (tareaId, grupoId) => {
    const response = await api.post(`/auth/tareas/${tareaId}/grupos/${grupoId}/salir/`);
    return response.data;
  },
  cerrarIngresoGrupo: async (tareaId, grupoId, cerrado) => {
    const response = await api.post(`/auth/tareas/${tareaId}/grupos/${grupoId}/cerrar-ingreso/`, {
      cerrado,
    });
    return response.data;
  },
};

// Servicios para gestión de clases
export const clasesService = {
  // Listar clases
  getClases: async () => {
    const response = await api.get('/auth/clases/');
    return response.data;
  },

  // Clases de cursos con el mismo código (para reutilizar contenido/material)
  getClasesReutilizables: async (cursoId, excluirId = null) => {
    const params = { curso_id: cursoId };
    if (excluirId) params.excluir_id = excluirId;
    const response = await api.get('/auth/clases/reutilizables/', { params });
    return response.data;
  },

  // Obtener clase por ID
  getClase: async (claseId) => {
    const response = await api.get(`/auth/clases/${claseId}/`);
    return response.data;
  },

  // Crear clase. claseData puede ser objeto JSON o FormData. options: { onUploadProgress(percent) }
  createClase: async (claseData, options = {}) => {
    const config = claseData instanceof FormData ? { headers: { 'Content-Type': false } } : {};
    if (options.onUploadProgress) {
      config.onUploadProgress = (e) => {
        if (e.lengthComputable) options.onUploadProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    const response = await api.post('/auth/clases/create/', claseData, config);
    return response.data;
  },

  // Actualizar clase. options: { onUploadProgress(percent) }
  updateClase: async (claseId, claseData, options = {}) => {
    const config = claseData instanceof FormData ? { headers: { 'Content-Type': false } } : {};
    if (options.onUploadProgress) {
      config.onUploadProgress = (e) => {
        if (e.lengthComputable) options.onUploadProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    const response = await api.patch(`/auth/clases/${claseId}/update/`, claseData, config);
    return response.data;
  },

  // Publicar clase
  publicarClase: async (claseId) => {
    const response = await api.post(`/auth/clases/${claseId}/publicar/`);
    return response.data;
  },

  // Eliminar clase
  deleteClase: async (claseId) => {
    const response = await api.delete(`/auth/clases/${claseId}/delete/`);
    return response.data;
  },
};

// Servicios para planilla de asistencia (Profesor, Tutor, Pasante, Ayudante)
export const asistenciaService = {
  getCursosAsistencia: async () => {
    const response = await api.get('/auth/asistencia/cursos/');
    return response.data;
  },
  getPlanilla: async (cursoId) => {
    const response = await api.get(`/auth/asistencia/planilla/${cursoId}/`);
    return response.data;
  },
  updateAsistenciaManual: async (cursoId, asistenciaManual) => {
    const response = await api.patch(`/auth/asistencia/manual/${cursoId}/`, { asistencia_manual: asistenciaManual });
    return response.data;
  },
  habilitarDia: async (cursoId, fecha) => {
    const response = await api.post(`/auth/asistencia/manual-dia/${cursoId}/`, { fecha });
    return response.data;
  },
  deshabilitarDia: async (cursoId, fecha) => {
    const response = await api.delete(`/auth/asistencia/manual-dia/${cursoId}/`, { params: { fecha } });
    return response.data;
  },
};

// Servicios para Mi Asistencia (estudiantes marcar presente)
export const miAsistenciaService = {
  getCursos: async () => {
    const response = await api.get('/auth/mi-asistencia/cursos/');
    return response.data;
  },
  marcar: async (cursoId, fecha) => {
    const response = await api.post('/auth/mi-asistencia/marcar/', { curso_id: cursoId, fecha });
    return response.data;
  },
};

// Chat (salas y mensajes)
export const chatService = {
  getSalas: async () => {
    const response = await api.get('/chat/salas/');
    return response.data;
  },
  getMensajes: async (salaId) => {
    const response = await api.get(`/chat/salas/${salaId}/mensajes/`);
    return response.data;
  },
  enviarMensaje: async (salaId, texto, rol) => {
    const response = await api.post(`/chat/salas/${salaId}/mensajes/enviar/`, { texto, rol: rol || undefined });
    return response.data;
  },
  heartbeat: async (salaId) => {
    await api.post(`/chat/salas/${salaId}/heartbeat/`);
  },
  getUsuariosOnline: async (salaId) => {
    const response = await api.get(`/chat/salas/${salaId}/usuarios-online/`);
    return response.data;
  },
  getModeracion: async (salaId) => {
    const response = await api.get(`/chat/salas/${salaId}/moderacion/`);
    return response.data;
  },
  patchModeracion: async (salaId, data) => {
    const response = await api.patch(`/chat/salas/${salaId}/moderacion/`, data);
    return response.data;
  },
  silenciarUsuario: async (salaId, userId) => {
    const response = await api.post(`/chat/salas/${salaId}/moderacion/silenciar-usuario/${userId}/`);
    return response.data;
  },
  desilenciarUsuario: async (salaId, userId) => {
    await api.delete(`/chat/salas/${salaId}/moderacion/desilenciar-usuario/${userId}/`);
  },
  ocultarMensaje: async (salaId, mensajeId) => {
    const response = await api.post(`/chat/salas/${salaId}/mensajes/${mensajeId}/ocultar/`);
    return response.data;
  },
};

// Configuración (solo Coordinador): plantillas de correo
export const configuracionService = {
  getPlantillasMail: async () => {
    const response = await api.get('/auth/configuracion/plantillas-mail/');
    return response.data;
  },
  getPlantillaMail: async (id) => {
    const response = await api.get(`/auth/configuracion/plantillas-mail/${id}/`);
    return response.data;
  },
  updatePlantillaMail: async (id, data) => {
    const hasFile = data.imagen instanceof File;
    const quitarImagen = data.quitar_imagen === true;
    if (hasFile || quitarImagen) {
      const form = new FormData();
      if (data.asunto !== undefined) form.append('asunto', data.asunto);
      if (data.cuerpo !== undefined) form.append('cuerpo', data.cuerpo);
      if (data.activo !== undefined) form.append('activo', data.activo);
      if (hasFile) form.append('imagen', data.imagen);
      if (quitarImagen) form.append('quitar_imagen', 'true');
      const response = await api.patch(`/auth/configuracion/plantillas-mail/${id}/editar/`, form);
      return response.data;
    }
    const response = await api.patch(`/auth/configuracion/plantillas-mail/${id}/editar/`, data);
    return response.data;
  },
  getPlantillasCertificado: async () => {
    const response = await api.get('/auth/configuracion/plantillas-certificado/');
    return response.data;
  },
  getPlantillaCertificado: async (id) => {
    const response = await api.get(`/auth/configuracion/plantillas-certificado/${id}/`);
    return response.data;
  },
  createPlantillaCertificado: async ({ nombre, campos, activo, imagen }) => {
    const form = new FormData();
    form.append('nombre', nombre || '');
    form.append('campos', JSON.stringify(campos || {}));
    if (activo !== undefined) form.append('activo', activo ? 'true' : 'false');
    if (imagen instanceof File) form.append('imagen', imagen);
    const response = await api.post('/auth/configuracion/plantillas-certificado/', form);
    return response.data;
  },
  updatePlantillaCertificado: async (id, data) => {
    const form = new FormData();
    if (data.nombre !== undefined) form.append('nombre', data.nombre);
    if (data.campos !== undefined) form.append('campos', JSON.stringify(data.campos || {}));
    if (data.activo !== undefined) form.append('activo', data.activo ? 'true' : 'false');
    if (data.quitar_imagen) form.append('quitar_imagen', 'true');
    if (data.imagen instanceof File) form.append('imagen', data.imagen);
    const response = await api.patch(`/auth/configuracion/plantillas-certificado/${id}/`, form);
    return response.data;
  },
  deletePlantillaCertificado: async (id) => {
    await api.delete(`/auth/configuracion/plantillas-certificado/${id}/`);
  },
  getFuentesCertificado: async () => {
    const response = await api.get('/auth/configuracion/fuentes-certificado/');
    return response.data;
  },
  uploadFuenteCertificado: async ({ archivo, nombre }) => {
    const form = new FormData();
    form.append('archivo', archivo);
    if (nombre) form.append('nombre', nombre);
    const response = await api.post('/auth/configuracion/fuentes-certificado/', form);
    return response.data;
  },
  importFuenteGoogle: async ({ family, weight = '400', nombre }) => {
    const response = await api.post('/auth/configuracion/fuentes-certificado/importar-google/', {
      family,
      weight,
      nombre: nombre || undefined,
    });
    return response.data;
  },
  deleteFuenteCertificado: async (pk) => {
    await api.delete(`/auth/configuracion/fuentes-certificado/${pk}/`);
  },
  getCronConfig: async () => {
    const response = await api.get('/auth/configuracion/cron/');
    return response.data;
  },
  updateCronActivo: async (codigo, activo) => {
    const response = await api.patch(`/auth/configuracion/cron/${encodeURIComponent(codigo)}/`, { activo });
    return response.data;
  },
  getRegistroConfig: async () => {
    const response = await api.get('/auth/configuracion/registro/');
    return response.data;
  },
  updateRegistroConfig: async (data) => {
    const response = await api.patch('/auth/configuracion/registro/', data);
    return response.data;
  },
};

// Configuración de registro (pública, sin auth) — para /register y Navbar
export const registroConfigService = {
  get: async () => {
    const response = await api.get('/config/registro/');
    return response.data;
  },
};

// Preferencias de notificaciones (estudiante, mi-configuracion)
export const preferenciasNotificacionesService = {
  get: async () => {
    const response = await api.get('/auth/preferencias-notificaciones/');
    return response.data;
  },
  update: async (data) => {
    const response = await api.patch('/auth/preferencias-notificaciones/', data);
    return response.data;
  },
};

// Notificaciones in-app (campana del panel principal)
export const notificacionesService = {
  list: async ({ rol, page = 1, page_size = 20 } = {}) => {
    const params = { page, page_size };
    if (rol) params.rol = rol;
    const response = await api.get('/auth/notificaciones/', { params });
    return response.data;
  },
  getUnreadCount: async (rol) => {
    const params = {};
    if (rol) params.rol = rol;
    const response = await api.get('/auth/notificaciones/unread-count/', { params });
    return response.data;
  },
  marcarLeida: async (id) => {
    const response = await api.post(`/auth/notificaciones/${id}/leida/`);
    return response.data;
  },
  marcarTodas: async (rol) => {
    const response = await api.post('/auth/notificaciones/marcar-todas/', { rol: rol || '' });
    return response.data;
  },
};

export default api;



